/* ==========================================================================
   lh-tracking.js — interaction tracking for LionHead Moto
   --------------------------------------------------------------------------
   Loaded on every page from snippets/lh-tracking.liquid.

   What it reports
   - WhatsApp clicks (floating bubble, product stock check, contact, about,
     shipping, exchange and guide pages) with the page, the button and the
     chat link.
   - Phone, driving-direction, email, marketplace and Telegram clicks.

   Every event carries the same context: page location, page type, button
   label, button location, the link itself, whether the visit arrived from a
   Google Ads click (gclid/wbraid/gbraid) or another ad platform, and — on
   product pages — the product and the size/colour the visitor was looking at.

   Where it goes
   - Straight to GA4 through the Google tag that the store already loads
     (gtag). If that tag is unavailable the event is pushed to dataLayer.
   - It is the only place on the site that fires these events, so nothing is
     counted twice.

   How to extend
   - Add data-lh-location="..." to any container to name the spot a button
     sits in.
   - Call window.lhTrack('event_name', { label, location, url }) from theme
     JavaScript for actions that are not plain links.

   Debug
   - Add ?lhtrack=1 to any URL to see every event in the browser console.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.LHTrackConfig || {};
  var DEBUG = /[?&]lhtrack=1(&|$)/.test(window.location.search);

  var SESSION_ATTR_KEY = 'lh_attr_session';
  var FIRST_ATTR_KEY   = 'lh_attr_first';
  var LANDING_KEY      = 'lh_landing_page';
  var CLICK_ID_KEYS    = ['gclid', 'wbraid', 'gbraid', 'msclkid', 'fbclid', 'ttclid'];

  var sessionStore = null;
  var localStore   = null;
  try { sessionStore = window.sessionStorage; } catch (e) { /* blocked */ }
  try { localStore   = window.localStorage; }   catch (e) { /* blocked */ }

  /* ── small helpers ─────────────────────────────────────────────────────── */

  function storeGet(store, key) {
    try { return store ? store.getItem(key) : null; } catch (e) { return null; }
  }

  function storeSet(store, key, value) {
    try { if (store) store.setItem(key, value); } catch (e) { /* ignore */ }
  }

  function clean(value, max) {
    if (value === null || value === undefined) return '';
    var text = String(value).replace(/\s+/g, ' ').trim();
    if (max && text.length > max) text = text.slice(0, max - 1).trim() + '…';
    return text;
  }

  function param(name) {
    try {
      if (typeof window.URLSearchParams !== 'function') return '';
      return new URLSearchParams(window.location.search).get(name) || '';
    } catch (e) { return ''; }
  }

  function log(name, params) {
    if (!DEBUG) return;
    try { console.info('[lh-track]', name, params); } catch (e) { /* ignore */ }
  }

  /* ── ad click attribution (kept for the whole visit) ───────────────────── */

  function readAttr(store, key) {
    try { return JSON.parse(storeGet(store, key) || 'null'); } catch (e) { return null; }
  }

  function captureAttribution() {
    var found = '';
    for (var i = 0; i < CLICK_ID_KEYS.length; i++) {
      if (param(CLICK_ID_KEYS[i])) { found = CLICK_ID_KEYS[i]; break; }
    }
    if (found) {
      storeSet(sessionStore, SESSION_ATTR_KEY, JSON.stringify({ click_id: found, ts: Date.now() }));
      if (!readAttr(localStore, FIRST_ATTR_KEY)) {
        storeSet(localStore, FIRST_ATTR_KEY, JSON.stringify({ click_id: found, ts: Date.now() }));
      }
    }
    if (!storeGet(sessionStore, LANDING_KEY)) {
      storeSet(sessionStore, LANDING_KEY, window.location.pathname + window.location.search);
    }
  }

  function adClick() {
    var attr = readAttr(sessionStore, SESSION_ATTR_KEY) || readAttr(localStore, FIRST_ATTR_KEY) || {};
    var type = attr.click_id ? String(attr.click_id) : '';
    return {
      ads_click: type ? 'yes' : 'no',
      click_id_type: type || 'none'
    };
  }

  /* ── GA4 session id (lets a report find the visit behind an event) ─────── */

  function gaSessionId() {
    try {
      var match = document.cookie.match(/_ga_[A-Z0-9]+=GS\d+\.\d+\.s(\d+)/i);
      return match ? match[1] : '';
    } catch (e) { return ''; }
  }

  /* ── page context ──────────────────────────────────────────────────────── */

  var PAGE_TYPE_LABEL = {
    home: 'Homepage',
    product: 'Product page',
    collection: 'Collection page',
    'list-collections': 'Collections index',
    page: 'Page',
    cart: 'Cart',
    search: 'Search results',
    blog: 'Blog',
    article: 'Guide',
    '404': 'Not found page',
    policy: 'Policy page'
  };

  function pageType() {
    return clean(CFG.pageType || '', 40) || 'page';
  }

  function baseParams() {
    var params = {
      page_location: window.location.href,
      page_type: pageType(),
      page_label: PAGE_TYPE_LABEL[pageType()] || ''
    };
    var ads = adClick();
    params.ads_click = ads.ads_click;
    params.click_id_type = ads.click_id_type;
    var sessionId = gaSessionId();
    if (sessionId) params.ga_session_id = sessionId;
    if (CFG.productTitle) {
      params.product_title = clean(CFG.productTitle, 100);
      params.product_handle = clean(CFG.productHandle, 100);
      if (CFG.productPrice) params.product_price = clean(CFG.productPrice, 40);
    } else if (CFG.collectionTitle) {
      params.collection_title = clean(CFG.collectionTitle, 100);
    }
    return params;
  }

  /* ── which button, and what it says ────────────────────────────────────── */

  var LOCATION_BY_ID = {
    'lhWaBubble': 'floating bubble',
    'lh-whatsapp-bubble-chat': 'floating bubble',
    'lh-contact-whatsapp-question': 'contact page',
    'lh-contact-whatsapp-advice': 'contact page',
    'lh-contact-shopee': 'contact page',
    'lh-contact-carousell': 'contact page'
  };

  function classesOf(node) {
    return typeof node.className === 'string' ? node.className : '';
  }

  // Markers that name the spot exactly, wherever they sit in the tree.
  function preciseLocation(node) {
    var explicit = node.getAttribute && node.getAttribute('data-lh-location');
    if (explicit) return clean(explicit, 60);
    if (LOCATION_BY_ID[node.id]) return LOCATION_BY_ID[node.id];
    var cls = classesOf(node);
    if (cls.indexOf('lh-about-btn-wa') !== -1) return 'about page';
    if (node.tagName === 'FOOTER' || /(^|\s)lh-footer/.test(cls)) return 'footer';
    if (/(^|\s)lh-header/.test(cls)) return 'header';
    return '';
  }

  // Last resort when a spot has no marker and no heading: the shared info-page button.
  function genericLocation(node) {
    return classesOf(node).indexOf('lh-info-btn-primary') !== -1 ? 'info page' : '';
  }

  function containerOf(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.tagName === 'SECTION' && node.id && node.id.indexOf('shopify-section-') === 0) return node;
      node = node.parentNode;
    }
    return null;
  }

  function locationOf(el, fallback) {
    var node = el;
    while (node && node.nodeType === 1) {
      var precise = preciseLocation(node);
      if (precise) return precise;
      node = node.parentNode;
    }
    // Name the on-page spot by its section heading, so a new page needs no code.
    var container = el ? containerOf(el) : null;
    if (container) {
      var heading = container.querySelector('h1, h2, .lh-info-hero-title');
      if (heading) return clean(heading.textContent, 60);
    }
    node = el;
    while (node && node.nodeType === 1) {
      var generic = genericLocation(node);
      if (generic) return generic;
      node = node.parentNode;
    }
    return clean(fallback, 60) || (PAGE_TYPE_LABEL[pageType()] || 'page');
  }

  function labelOf(el) {
    if (!el) return '';
    var text = clean(el.textContent, 60);
    if (text) return text;
    return clean(el.getAttribute('aria-label') || el.getAttribute('title') || '', 60);
  }

  /* ── event names ───────────────────────────────────────────────────────── */

  var NAMES = {
    call: 'call_click',
    directions: 'directions_click',
    email: 'email_click',
    marketplace: 'marketplace_click',
    telegram: 'telegram_click'
  };

  function whatsappName(location) {
    var l = String(location || '').toLowerCase();
    if (l.indexOf('floating') !== -1) return 'whatsapp_chat_open';   // long-standing name, kept
    if (l.indexOf('product') !== -1 || l.indexOf('stock') !== -1) return 'whatsapp_stock_check';
    if (l.indexOf('contact') !== -1) return 'whatsapp_contact_page';
    if (l.indexOf('about') !== -1) return 'whatsapp_about_page';
    if (l.indexOf('shipping') !== -1) return 'whatsapp_shipping_page';
    if (l.indexOf('exchange') !== -1) return 'whatsapp_exchange_page';
    if (l.indexOf('guide') !== -1 || l.indexOf('psb') !== -1) return 'whatsapp_guide_page';
    return 'whatsapp_site_click';
  }

  var HOST_RULES = [
    { action: 'whatsapp',    test: /(^|\.)wa\.me$|(^|\.)whatsapp\.com$/i },
    { action: 'telegram',    test: /(^|\.)t\.me$|(^|\.)telegram\.(me|org)$/i },
    { action: 'marketplace', test: /(^|\.)shopee\.[a-z.]+$|(^|\.)carousell\.[a-z.]+$/i },
    { action: 'directions',  test: /(^|\.)maps\.app\.goo\.gl$|(^|\.)google\.com$|(^|\.)maps\.google\.com$/i }
  ];

  function classify(href, host) {
    if (!href) return '';
    if (/^tel:/i.test(href)) return 'call';
    if (/^mailto:/i.test(href)) return 'email';
    if (!host) return '';
    for (var i = 0; i < HOST_RULES.length; i++) {
      var rule = HOST_RULES[i];
      if (rule.test.test(host)) {
        // google.com is only a directions link when it is a Maps URL.
        if (rule.action === 'directions' && /(^|\.)google\.com$/i.test(host) && href.indexOf('/maps') === -1) return '';
        return rule.action;
      }
    }
    return '';
  }

  function hostOf(url) {
    try {
      var parsed = new URL(url, window.location.href);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
      return parsed.hostname.replace(/^www\./, '');
    } catch (e) { return ''; }
  }

  /* ── send ──────────────────────────────────────────────────────────────── */

  function send(name, params) {
    log(name, params);
    try {
      if (typeof window.gtag === 'function') { window.gtag('event', name, params); return; }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, lh_params: params });
    } catch (e) { /* never break the page */ }
  }

  /* ── public entry point ────────────────────────────────────────────────── */

  function track(action, detail) {
    detail = detail || {};
    var el = detail.el || null;
    var href = detail.url || (el && el.getAttribute ? (el.href || el.getAttribute('href') || '') : '');
    var host = hostOf(href);
    var location = detail.location || locationOf(el, detail.fallbackLocation) || '';
    var name = detail.event || (action === 'whatsapp' ? whatsappName(location) : NAMES[action]);
    if (!name) return;

    var params = baseParams();
    params.action_location = clean(location, 60);
    params.action_label = clean(detail.label || labelOf(el), 60);
    if (href) {
      params.link_url = clean(href, 100);
      params.link_text = params.action_label;
      if (host) params.link_domain = clean(host, 60);
    }
    if (detail.variant) params.product_variant = clean(detail.variant, 60);
    send(name, params);
  }

  window.lhTrack = track;

  /* ── catch plain links automatically ───────────────────────────────────── */

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button > 1) return;
    var node = event.target;
    while (node && node.nodeType === 1 && node.tagName !== 'A') node = node.parentNode;
    if (!node || node.tagName !== 'A') return;
    var href = node.getAttribute('href') || '';
    var host = hostOf(href);
    var action = classify(href, host);
    if (!action) return;
    track(action, { el: node, url: node.href || href });
  }, true);

  captureAttribution();
})();
